/**
 * Attaches drop performance tags (drop-performance-tags API) to generic drop rows.
 * Tags are keyed by the Voluum CV3 / Binom token_3 value — the same key the drops
 * report joins on (row.voluumCampaignName), with the drop uniqueId as fallback.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';

import { useDropPerformanceTags } from '@/hooks';

import type { GenericStatsRow } from '../../shared/generic-stats.types';

export interface DropPerfTagRowsResult {
  rowsWithTags: GenericStatsRow[];
  tagFilterOptions: string[];
  tagFilterColors: Record<string, string> | undefined;
  /** Rule name -> emoji ("+" / "-") for the drops tag display. */
  tagEmojis: Record<string, string> | undefined;
  /** Rules with drop-scoped (source_* or list_*) conditions — bare emoji,
   *  shown only in the ungrouped view. */
  scopedRuleNames: string[] | undefined;
  tagsLoading: boolean;
}

function lookupTags(
  tagsByDropId: Record<string, string[]>,
  keys: Array<string | undefined | null>,
): string[] {
  for (const key of keys) {
    const k = (key ?? '').trim();
    if (!k) continue;
    const direct = tagsByDropId[k];
    if (direct) return direct;
    const entry = Object.entries(tagsByDropId).find(
      ([id]) => id.toLowerCase() === k.toLowerCase(),
    );
    if (entry) return entry[1];
  }
  return [];
}

export function useDropPerfTagRows(
  countryKey: string,
  wiserIds: number[],
  range: { from?: string; to?: string },
  currency: string,
  allGenericRows: GenericStatsRow[],
): DropPerfTagRowsResult {
  const perfTagsQuery = useDropPerformanceTags(
    countryKey,
    wiserIds.length > 0 ? wiserIds : undefined,
    range.from && range.to
      ? { from: range.from, to: range.to, tz: 'Europe/Paris', currency }
      : undefined,
  );
  const tagsByDropId = useMemo(
    () => perfTagsQuery.data?.tagsByDropId ?? {},
    [perfTagsQuery.data?.tagsByDropId],
  );
  const tagColors = perfTagsQuery.data?.tagColors;

  /** Deduped union — source_* and list_* rules share the display semantics. */
  const scopedRuleNames = useMemo(() => {
    const names = new Set([
      ...(perfTagsQuery.data?.sourceConditionRuleNames ?? []),
      ...(perfTagsQuery.data?.listConditionRuleNames ?? []),
    ]);
    return names.size > 0 ? [...names] : undefined;
  }, [
    perfTagsQuery.data?.sourceConditionRuleNames,
    perfTagsQuery.data?.listConditionRuleNames,
  ]);

  // Rows always carry the FULL tag set — the tag filter matches on it. Hiding
  // source-rule tags in grouped views happens at render time (renderFirstCell).
  const rowsWithTags = useMemo(
    () =>
      allGenericRows.map((row) => ({
        ...row,
        performanceTags: lookupTags(tagsByDropId, [
          row._originalDrop?.voluumCampaignName,
          row.id,
        ]),
      })),
    [allGenericRows, tagsByDropId],
  );

  const tagFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const name of Object.keys(tagColors ?? {})) if (name.trim()) set.add(name.trim());
    for (const tags of Object.values(tagsByDropId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagColors, tagsByDropId]);

  return {
    rowsWithTags,
    tagFilterOptions,
    tagFilterColors: tagColors,
    tagEmojis: perfTagsQuery.data?.tagEmojis,
    scopedRuleNames: scopedRuleNames,
    tagsLoading: perfTagsQuery.isLoading,
  };
}
